import {
    type RefObject,
    useCallback,
    useEffect,
    useRef,
    useState,
} from "react";
import { Key } from "ts-key-enum";

import { getDocument, getWindow } from "@thelabnyc/thelabui/src/utils/dom";

import { RICH_NAVIGATION_MOBILE_PANEL_ID } from "../models/rich-navigation";
import {
    focusElement,
    getFocusableElems,
    trapFocus,
} from "../utils/keyboardFocus";

const focusInitialDialogTarget = (headerElem: HTMLElement): void => {
    const closeButton = headerElem.querySelector<HTMLElement>(
        ".mobile-toggle.open",
    );
    if (closeButton) {
        focusElement(closeButton);
        return;
    }

    const firstFocusable = getFocusableElems(headerElem)[0];
    if (firstFocusable) {
        focusElement(firstFocusable);
        return;
    }

    headerElem.setAttribute("tabindex", "-1");
    focusElement(headerElem);
    headerElem.removeAttribute("tabindex");
};

const setBodyMinHeight = (navElem: HTMLElement) => {
    const window = getWindow();
    const document = getDocument();
    if (!window || !document) {
        return;
    }
    const collectionContent =
        navElem.querySelector<HTMLElement>(
            ".rich-navigation--link-collection .collection[aria-hidden=false]",
        ) || navElem;
    const navRect = navElem.getBoundingClientRect();
    const collectionRect = collectionContent.getBoundingClientRect();
    const newBodyMinHeight =
        window.scrollY + Math.max(navRect.bottom, collectionRect.bottom) + 20; // Add 20px to allow for the box shadow
    document.body.style.minHeight = `${newBodyMinHeight}px`;
};

const displayRichNavigationElem = (hide: string) => {
    const document = getDocument();
    if (!document) {
        return;
    }
    const navElem = document.querySelector<HTMLElement>(
        `#${RICH_NAVIGATION_MOBILE_PANEL_ID}.rich-navigation, nav.rich-navigation`,
    );
    if (navElem) {
        navElem.setAttribute("aria-hidden", hide);
        setBodyMinHeight(navElem);
    }
};

// Clears the rich-navigation panel's aria-hidden + body scroll-lock state.
const closeRichNav = () => {
    const document = getDocument();
    if (!document) {
        return;
    }
    document
        .querySelector<HTMLElement>(".mobile-toggle")
        ?.classList.remove("open");
    displayRichNavigationElem("true");
    document.body.classList.remove("modal-open");
};

const stripRichNavigationAccordionAttributes = (root: ParentNode): void => {
    Array.from(
        root.querySelectorAll<HTMLElement>(".rich-navigation--link-collection"),
    ).forEach((linkCollection) => {
        linkCollection
            .querySelector<HTMLElement>("button")
            ?.removeAttribute("aria-expanded");
        linkCollection
            .querySelector<HTMLElement>(".collection")
            ?.removeAttribute("aria-hidden");
    });
};

export const useMobileMenuDialog = (options: {
    belowMobile: boolean;
    hasRichNavigation: boolean;
    // When true (default) the rich-navigation link collections have their
    // aria-expanded/aria-hidden attributes stripped as the dialog opens -- the
    // right behavior for a flat, all-expanded mobile menu (sealy/tempurpedic).
    // cocoon/one800pillows keep a genuine one-at-a-time accordion whose aria
    // state React owns, so they opt out to leave those attributes intact.
    stripAccordionAttributes?: boolean;
}): {
    siteHeaderRef: RefObject<HTMLElement | null>;
    mobilePanelVisible: boolean;
    showMobileMenuDialog: boolean;
    openMobilePanel: (triggerElement?: HTMLElement) => void;
    closeMobilePanel: () => void;
} => {
    const [mobilePanelVisible, setMobilePanelVisible] = useState(false);
    const siteHeaderRef = useRef<HTMLElement | null>(null);
    const menuTriggerRef = useRef<HTMLElement | null>(null);
    const mobilePanelVisibleRef = useRef(false);
    const closeMobilePanelRef = useRef<() => void>(() => {});
    const belowMobileRef = useRef(options.belowMobile);

    mobilePanelVisibleRef.current = mobilePanelVisible;
    belowMobileRef.current = options.belowMobile;

    const restoreMenuTriggerFocus = useCallback(() => {
        const trigger = menuTriggerRef.current;
        menuTriggerRef.current = null;
        const document = getDocument();
        if (!document) {
            return;
        }
        if (trigger && document.contains(trigger)) {
            focusElement(trigger);
            return;
        }
        document.body.setAttribute("tabindex", "-1");
        focusElement(document.body);
        document.body.removeAttribute("tabindex");
    }, []);

    const closeMobilePanel = useCallback(() => {
        closeRichNav();
        getDocument()?.body.classList.remove("modal-open");
        setMobilePanelVisible(false);
        restoreMenuTriggerFocus();
    }, [restoreMenuTriggerFocus]);

    closeMobilePanelRef.current = closeMobilePanel;

    const openMobilePanel = useCallback(
        (triggerElement?: HTMLElement) => {
            // A trigger can stay mounted (e.g. sealy's always-rendered
            // hamburger, kept for its mobile flex layout) on pages with no
            // active rich navigation; there is no panel to open there.
            if (!options.belowMobile || !options.hasRichNavigation) {
                return;
            }
            const document = getDocument();
            const candidate =
                triggerElement ??
                (document?.activeElement instanceof HTMLElement
                    ? document.activeElement
                    : null);
            menuTriggerRef.current = candidate;
            document?.body.classList.add("modal-open");
            setMobilePanelVisible(true);
        },
        [options.belowMobile, options.hasRichNavigation],
    );

    const showMobileMenuDialog =
        mobilePanelVisible && options.belowMobile && options.hasRichNavigation;

    useEffect(() => {
        if (!showMobileMenuDialog || !siteHeaderRef.current) {
            return;
        }
        const headerElem = siteHeaderRef.current;
        const mobilePanel = headerElem.querySelector(
            `#${RICH_NAVIGATION_MOBILE_PANEL_ID}`,
        );
        if (mobilePanel && (options.stripAccordionAttributes ?? true)) {
            stripRichNavigationAccordionAttributes(mobilePanel);
        }
        focusInitialDialogTarget(headerElem);
        const onKeyDown = (event: KeyboardEvent) => {
            if (event.key === Key.Escape) {
                event.preventDefault();
                closeMobilePanelRef.current();
            }
        };
        headerElem.addEventListener("keydown", onKeyDown);
        const releaseTrap = trapFocus(headerElem);
        return () => {
            headerElem.removeEventListener("keydown", onKeyDown);
            releaseTrap();
        };
    }, [showMobileMenuDialog, options.stripAccordionAttributes]);

    useEffect(() => {
        return () => {
            getDocument()?.body.classList.remove("modal-open");
            closeRichNav();
        };
    }, []);

    useEffect(() => {
        const window = getWindow();
        if (!window) {
            return;
        }
        const resizeHandler = () => {
            if (belowMobileRef.current) {
                return;
            }
            if (mobilePanelVisibleRef.current) {
                closeMobilePanelRef.current();
            }
            // On desktop `#mobile-panel` stays mounted as the visible nav, but
            // the imperative mobile-close path (`closeRichNav`) leaves
            // `aria-hidden="true"` on it, which would hide the desktop
            // navigation from assistive tech until reload. React never owns this
            // attribute, so clear it here after closing (mirrors the selector
            // `displayRichNavigationElem` sets it on).
            getDocument()
                ?.querySelector<HTMLElement>(
                    `#${RICH_NAVIGATION_MOBILE_PANEL_ID}.rich-navigation, nav.rich-navigation`,
                )
                ?.removeAttribute("aria-hidden");
        };
        resizeHandler();
        window.addEventListener("resize", resizeHandler);
        return () => {
            window.removeEventListener("resize", resizeHandler);
        };
    }, [options.belowMobile]);

    return {
        siteHeaderRef,
        mobilePanelVisible,
        showMobileMenuDialog,
        openMobilePanel,
        closeMobilePanel,
    };
};
